import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { WmPart } from "../lib/wmParts";
import api from "../lib/api";
import SortableGrid from "../components/SortableGrid";
import { Link } from "react-router-dom";
import { fmtCur, fmtNum, fmtDate } from "../lib/format";
import { useCurrencyMode } from "../lib/useCurrencyMode";
import { ExportXlsxButton } from "../components/ExportXlsxButton";
import { xlsxDate } from "../lib/exportXlsx";
import { Clock, Warning, CheckCircle, Timer, ChartLineUp, Info, Sparkle, MagnifyingGlass, X } from "@phosphor-icons/react";

const STATUS_META = {
  late:     { label: "Late",      badge: "closed",    color: "#B91C1C" },
  on_time:  { label: "On Time",   badge: "confirmed", color: "#0F766E" },
  overdue:  { label: "Overdue",   badge: "closed",    color: "#B91C1C" },
  due_soon: { label: "Due Soon",  badge: "partial",   color: "#B45309" },
  on_track: { label: "On Track",  badge: "confirmed", color: "#0F766E" },
};

function otdDelta(shipDate, dueDate, offset) {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  if (isNaN(due)) return null;
  const target = new Date(due);
  target.setDate(target.getDate() - (Number(offset) || 0));
  const ref = shipDate ? new Date(shipDate) : new Date();
  if (isNaN(ref)) return null;
  const delta = Math.round((ref - target) / 86400000);
  const shipped = !!shipDate;
  if (delta > 0) return { label: shipped ? `${delta}d late` : `${delta}d overdue`, color: "#B91C1C" };
  if (delta < 0) return { label: shipped ? `${Math.abs(delta)}d early` : `${Math.abs(delta)}d left`, color: shipped ? "#1E40AF" : "#0F766E" };
  return { label: shipped ? "on time" : "due today", color: "#0F766E" };
}

// The dispatch TARGET = customer due date minus the ex-factory offset (same shift the OTD
// countdown and the header use). Keeps the line TARGET column consistent with the header.
function offsetTarget(dueDate, offset) {
  if (!dueDate) return null;
  const d = new Date(dueDate);
  if (isNaN(d)) return null;
  d.setDate(d.getDate() - (Number(offset) || 0));
  return d.toISOString().slice(0, 10);
}

function OtdLineDetail({ pono, offsetDays, curCode }) {
  const [lines, setLines] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/ms/pos/${pono}`)
      .then((r) => { if (alive) setLines(r.data?.lines || []); })
      .catch(() => { if (alive) setLines([]); });
    return () => { alive = false; };
  }, [pono]);

  const th = { textAlign: "left", padding: "6px 10px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, color: "#64748B", borderBottom: "1px solid var(--jde-border)", whiteSpace: "nowrap" };
  const td = { padding: "7px 10px", fontSize: 12, borderBottom: "1px solid #EEF1F4", whiteSpace: "nowrap" };

  if (lines === null) return <div className="muted" style={{ padding: 14, fontSize: 12 }} data-testid={`otd-drill-loading-${pono}`}>Loading line items…</div>;
  if (lines.length === 0) return <div className="muted" style={{ padding: 14, fontSize: 12 }}>No line items on this PO.</div>;

  return (
    <div style={{ padding: "12px 18px 16px" }} data-testid={`otd-drill-${pono}`}>
      <div style={{ overflowX: "auto", border: "1px solid var(--jde-border)", borderRadius: 6, background: "white" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
          <thead>
            <tr>
              <th style={th}>#</th>
              <th style={th}>Item</th>
              <th style={th}>Description</th>
              <th style={{ ...th, textAlign: "right" }}>Ordered</th>
              <th style={{ ...th, textAlign: "right" }}>Shipped</th>
              <th style={{ ...th, textAlign: "right" }}>Open</th>
              <th style={th}>Due Date</th>
              <th style={th}>Target</th>
              <th style={th}>Ship Date</th>
              <th style={{ ...th, textAlign: "center" }}>OTD</th>
              <th style={{ ...th, textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              // Mirror the backend PO-row OTD definition so the drill-down never contradicts the
              // header: a line counts as SHIPPED (dispatch-vs-target OTD) only when it's FULLY
              // shipped (qty_shipped>0 AND nothing open). A partially-shipped line still carries
              // open-delivery risk on its remaining qty → show that (today-vs-target), consistent
              // with the header's "overdue / not shipped".
              const qtyShipped = Number(l.qty_shipped) || 0;
              const qtyOpen = l.qty_open != null
                ? Number(l.qty_open)
                : Math.max(0, (Number(l.qty_ordered) || 0) - qtyShipped);
              const fullyShipped = qtyShipped > 0 && qtyOpen <= 0;
              const partial = qtyShipped > 0 && qtyOpen > 0;
              const o = otdDelta(fullyShipped ? l.dis_date : null, l.deliv_date, offsetDays);
              return (
                <tr key={i}>
                  <td style={td} className="text-mono">{i + 1}</td>
                  <td style={{ ...td, fontWeight: 700 }} className="text-mono">{l.icode || "—"}<WmPart icode={l.icode} /></td>
                  <td style={{ ...td, whiteSpace: "normal" }}>{l.name || "—"}</td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{fmtNum(l.qty_ordered)}</td>
                  <td style={{ ...td, textAlign: "right", color: qtyShipped > 0 ? "#166534" : "#94A3B8" }} className="text-mono">{fmtNum(l.qty_shipped)}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }} className="text-mono">{fmtNum(l.qty_open)}</td>
                  <td style={td} className="text-mono">{fmtDate(l.deliv_date)}</td>
                  <td style={td} className="text-mono">{fmtDate(offsetTarget(l.deliv_date, offsetDays) || l.target_date)}</td>
                  <td style={td} className="text-mono">
                    {l.dis_date
                      ? <>{fmtDate(l.dis_date)}{partial && <span className="muted" style={{ fontSize: 10, marginLeft: 4 }}>(partial)</span>}</>
                      : <span className="muted">not shipped</span>}
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {o ? <span className="text-mono" style={{ fontWeight: 700, color: o.color }}>{o.label}</span> : <span className="muted">—</span>}
                  </td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }} className="text-mono">{fmtCur(l.amt ?? (Number(l.qty_ordered) * Number(l.price || 0)), l.currency || curCode)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({ label, value, sub, color, active, onClick, icon: Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`otd-tile-${label.toLowerCase().replace(/\s|%/g, "-")}`}
      style={{
        textAlign: "left", cursor: onClick ? "pointer" : "default",
        background: active ? "#0F172A" : "white",
        borderTop: `1px solid ${active ? "#0F172A" : "var(--jde-border)"}`,
        borderRight: `1px solid ${active ? "#0F172A" : "var(--jde-border)"}`,
        borderBottom: `1px solid ${active ? "#0F172A" : "var(--jde-border)"}`,
        borderLeft: `4px solid ${color}`, padding: "12px 14px", borderRadius: 4,
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
        transition: "background 0.12s, border-color 0.12s",
      }}
    >
      <div>
        <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600,
                      color: active ? "#CBD5E1" : "var(--jde-text-muted, #64748B)" }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, marginTop: 3, fontFamily: "var(--jde-font-mono)",
                      color: active ? "#fff" : "#0F172A" }}>{value}</div>
        {sub && <div style={{ fontSize: 10.5, marginTop: 2, color: active ? "#94A3B8" : "var(--jde-text-muted, #64748B)" }}>{sub}</div>}
      </div>
      {Icon && <Icon size={26} weight="duotone" color={color} style={{ opacity: active ? 0.9 : 0.7 }} />}
    </button>
  );
}

export default function MsOtdRisk() {
  const [data, setData] = useState(null);
  const [plant, setPlant] = useState("");
  const [plants, setPlants] = useState([]);
  const [customer, setCustomer] = useState("");
  const [customers, setCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [viewFilter, setViewFilter] = useState("all"); // all | open (unshipped) | completed (shipped)
  const [nlQuery, setNlQuery] = useState("");
  const [nlSpec, setNlSpec] = useState(null);
  const [nlSummary, setNlSummary] = useState("");
  const [nlLoading, setNlLoading] = useState(false);
  const [nlError, setNlError] = useState("");
  const mode = useCurrencyMode();
  const curCode = (mode || "USD").toUpperCase() === "USD" ? "USD" : (mode || "").toUpperCase() || "INR";

  useEffect(() => {
    api.get("/ms/plants").then((r) => setPlants(r.data)).catch(() => {});
    api.get("/ms/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const qs = new URLSearchParams();
    if (plant) qs.set("plant", plant);
    if (customer) qs.set("customer", customer);
    if (mode) qs.set("mode", mode);
    setData(null);
    api.get(`/ms/otd-risk?${qs.toString()}`).then((r) => setData(r.data)).catch(() => setData({ counts: {}, rows: [] }));
  }, [plant, customer, mode]);

  const c = data?.counts || {};
  const rows = (data?.rows || [])
    .filter((r) => !statusFilter || r.status === statusFilter)
    .filter((r) => viewFilter === "all" || r.kind === viewFilter);
  const toggle = (s) => setStatusFilter((cur) => (cur === s ? "" : s));

  // Natural-language filter — the LLM (server) parses the sentence into a spec; we apply it here,
  // deterministically, ON TOP of the currently-filtered rows (plant / customer / status).
  const applyNl = (list, spec) => {
    if (!spec) return list;
    const cust = (spec.customers || []).map((s) => (s || "").toLowerCase());
    const po = (spec.po || "").toString().toLowerCase().replace(/\s/g, "");
    return list.filter((r) => {
      if (spec.kind && r.kind !== spec.kind) return false;
      if (cust.length || po) {
        const name = (r.customer_name || "").toLowerCase();
        const custHit = cust.length ? cust.some((x) => name.includes(x) || x.includes(name)) : false;
        const pos = [r.ms_pono, r.ms_custpo, r.cust_po2].map((x) => (x == null ? "" : String(x)).toLowerCase().replace(/\s/g, ""));
        const poHit = po ? pos.some((p) => p && p.includes(po)) : false;
        if (!(custHit || poHit)) return false;
      }
      if ((spec.statuses || []).length && !spec.statuses.includes(r.status)) return false;
      if (spec.within_days != null) {
        if (r.kind !== "open" || r.days_to_target == null) return false;
        if (r.days_to_target > spec.within_days) return false;
        if (!spec.include_overdue && r.days_to_target < 0) return false;
      } else if (spec.include_overdue && !(spec.statuses || []).length) {
        if (!(r.kind === "open" && (r.days_to_target || 0) < 0)) return false;
      }
      return true;
    });
  };

  const runNlQuery = async () => {
    const q = nlQuery.trim();
    if (!q) return;
    setNlLoading(true); setNlError("");
    try {
      const names = [...new Set((data?.rows || []).map((r) => r.customer_name).filter(Boolean))];
      const res = await api.post("/ms/otd-risk/nl-filter", { query: q, customers: names });
      setNlSpec(res.data.spec);
      setNlSummary(res.data.summary || "");
    } catch (e) {
      setNlError("Couldn't understand that — try rephrasing (e.g. \"overdue POs for Valmont within 7 days\").");
      setNlSpec(null); setNlSummary("");
    } finally {
      setNlLoading(false);
    }
  };

  const clearNl = () => { setNlSpec(null); setNlSummary(""); setNlQuery(""); setNlError(""); };
  const visibleRows = applyNl(rows, nlSpec);

  const exportColumns = [
    { header: "PO #", value: (r) => r.ms_pono || "" },
    { header: "Customer PO", value: (r) => r.ms_custpo || "" },
    { header: "End Customer PO Number", value: (r) => r.cust_po2 || "" },
    { header: "Customer", value: (r) => r.customer_name || "" },
    { header: "Offset (days)", value: (r) => Number(r.offset_days) || 0 },
    { header: "Status", value: (r) => (STATUS_META[r.status]?.label || r.status || "") },
    { header: "Due Date", value: (r) => xlsxDate(r.due_date) },
    { header: "Target", value: (r) => xlsxDate(r.target_date) },
    { header: "Ship / Invoice", value: (r) => xlsxDate(r.ship_date) },
    { header: "Days Late / To Target", value: (r) => r.kind === "completed" ? (Number(r.days_late) || 0) : (r.days_to_target ?? "") },
    { header: "PO Value", value: (r) => Number(r.amount) || 0 },
    { header: "Currency", value: (r) => r.currency_code || curCode },
  ];

  return (
    <Layout breadcrumb={["Home", "OTD & Delivery Risk"]}>
      <div className="jde-page">
        <div className="jde-toolbar" data-testid="otd-toolbar">
          <div className="jde-toolbar-section">
            <span className="muted" style={{ fontSize: 11, fontWeight: 600 }}>CUSTOMER</span>
            <select className="jde-input" value={customer} onChange={(e) => setCustomer(e.target.value)} data-testid="otd-filter-customer">
              <option value="">All customers</option>
              {customers.map((x, i) => <option key={`${x.ms_pcode || x.name}-${i}`} value={x.name}>{x.name}</option>)}
            </select>
          </div>
          <div className="jde-toolbar-section">
            <span className="muted" style={{ fontSize: 11, fontWeight: 600 }}>PLANT</span>
            <select className="jde-input" value={plant} onChange={(e) => setPlant(e.target.value)} data-testid="otd-filter-plant">
              <option value="">All plants</option>
              {plants.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="jde-toolbar-section" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="muted" style={{ fontSize: 11, fontWeight: 600 }}>SHOW</span>
            <div style={{ display: "inline-flex", border: "1px solid var(--jde-border)", borderRadius: 6, overflow: "hidden" }} data-testid="otd-view-filter">
              {[
                { key: "all", label: "All" },
                { key: "open", label: "Unshipped only" },
                { key: "completed", label: "Shipped only" },
              ].map((opt, i) => {
                const active = viewFilter === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setViewFilter(opt.key)}
                    data-testid={`otd-view-${opt.key}`}
                    style={{
                      padding: "5px 12px",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      border: "none",
                      borderLeft: i === 0 ? "none" : "1px solid var(--jde-border)",
                      background: active ? "var(--omh-primary)" : "white",
                      color: active ? "white" : "#334155",
                      transition: "background-color 0.15s ease, color 0.15s ease",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <span className="muted" style={{ fontSize: 11, marginLeft: "auto" }}>
            <Info size={12} weight="bold" style={{ verticalAlign: "-2px", marginRight: 3 }} />
            Target = customer due date − ex-factory offset (set per customer). Risk window: {data?.risk_window ?? 14}d.
          </span>
        </div>

        {/* Natural-language filter — works ON TOP of the customer / plant / status filters above */}
        <div className="jde-panel" style={{ marginTop: 12, padding: "12px 14px", background: "linear-gradient(180deg,#F8FAFF,#FFFFFF)", border: "1px solid #E2E8F0" }} data-testid="otd-nl-panel">
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Sparkle size={16} weight="fill" style={{ color: "#7C3AED", flexShrink: 0 }} />
            <input
              className="jde-input"
              style={{ flex: 1, minWidth: 260 }}
              placeholder='Ask in plain English — e.g. "shipments due in next 10 days for MPS York" or "overdue and within 7 day ship window for Valmont"'
              value={nlQuery}
              onChange={(e) => setNlQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runNlQuery(); }}
              data-testid="otd-nl-input"
            />
            <button className="jde-btn jde-btn-primary jde-btn-sm" onClick={runNlQuery} disabled={nlLoading || !nlQuery.trim()} data-testid="otd-nl-ask">
              <MagnifyingGlass size={13} weight="bold" style={{ verticalAlign: "-2px", marginRight: 4 }} />
              {nlLoading ? "Thinking…" : "Ask"}
            </button>
            {(nlSpec || nlError) && (
              <button className="jde-btn jde-btn-sm" onClick={clearNl} data-testid="otd-nl-clear">
                <X size={13} weight="bold" style={{ verticalAlign: "-2px", marginRight: 3 }} />Clear
              </button>
            )}
          </div>
          {nlError && <div style={{ color: "#B91C1C", fontSize: 12, marginTop: 8 }} data-testid="otd-nl-error">{nlError}</div>}
          {nlSpec && !nlError && (
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} data-testid="otd-nl-chip">
              <span className="muted" style={{ fontSize: 11 }}>Showing:</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#5B21B6", background: "#EDE9FE", border: "1px solid #DDD6FE", borderRadius: 999, padding: "3px 12px" }}>
                {nlSummary || "All rows"}
              </span>
              <span className="muted" style={{ fontSize: 11 }}>· {fmtNum(visibleRows.length)} match{visibleRows.length === 1 ? "" : "es"}</span>
            </div>
          )}
        </div>

        {!data ? (
          <div className="muted" style={{ padding: 24 }} data-testid="otd-loading">Computing OTD…</div>
        ) : (
          <>
            {/* OTD (completed) */}
            <div className="jde-section-title" style={{ fontSize: 12, fontWeight: 700, margin: "14px 0 8px", color: "#334155" }}>
              ON-TIME DELIVERY · {fmtNum(c.completed_total || 0)} shipped POs evaluated
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
              <Tile label="OTD %" value={c.otd_pct == null ? "—" : `${c.otd_pct}%`} sub="on-time / shipped"
                    color="#2A5C9B" icon={ChartLineUp} />
              <Tile label="On Time" value={fmtNum(c.on_time || 0)} sub="shipped on/before target"
                    color="#0F766E" icon={CheckCircle} active={statusFilter === "on_time"} onClick={() => toggle("on_time")} />
              <Tile label="Late" value={fmtNum(c.late || 0)} sub="shipped after target"
                    color="#B91C1C" icon={Warning} active={statusFilter === "late"} onClick={() => toggle("late")} />
            </div>

            {/* RISK (open) */}
            <div className="jde-section-title" style={{ fontSize: 12, fontWeight: 700, margin: "18px 0 8px", color: "#334155" }}>
              OPEN ORDER RISK · {fmtNum(c.open_total || 0)} open POs
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
              <Tile label="Overdue" value={fmtNum(c.overdue || 0)} sub="target already passed, not shipped"
                    color="#B91C1C" icon={Clock} active={statusFilter === "overdue"} onClick={() => toggle("overdue")} />
              <Tile label="Due Soon" value={fmtNum(c.due_soon || 0)} sub={`target within ${data?.risk_window ?? 14} days`}
                    color="#B45309" icon={Timer} active={statusFilter === "due_soon"} onClick={() => toggle("due_soon")} />
              <Tile label="On Track" value={fmtNum(c.on_track || 0)} sub="target comfortably ahead"
                    color="#0F766E" icon={CheckCircle} active={statusFilter === "on_track"} onClick={() => toggle("on_track")} />
            </div>

            {(c.no_due_date || 0) > 0 && (
              <div className="muted" style={{ fontSize: 11, marginTop: 10 }} data-testid="otd-no-date">
                <Info size={12} weight="bold" style={{ verticalAlign: "-2px", marginRight: 3 }} />
                {fmtNum(c.no_due_date)} PO(s) excluded — no due date on any line.
              </div>
            )}

            {/* DRILL-DOWN TABLE */}
            <div className="jde-panel" style={{ marginTop: 16 }}>
              <div className="jde-panel-header">
                <div className="jde-panel-title">
                  {statusFilter ? `${STATUS_META[statusFilter]?.label} POs` : (nlSpec ? "Matching POs" : "All evaluated POs")} ({fmtNum(visibleRows.length)})
                </div>
                {statusFilter && (
                  <button className="jde-btn jde-btn-sm" onClick={() => setStatusFilter("")} data-testid="otd-clear-filter">Clear filter</button>
                )}
                <ExportXlsxButton filename="otd-risk" columns={exportColumns} rows={visibleRows} sheetName="OTD & Risk" testid="otd-export-xlsx" />
              </div>
              <div className="jde-panel-body" style={{ padding: 0 }}>
                <SortableGrid pageSize={100}
                  columns={[
                    { key: "ms_pono", header: "PO #", render: (r) => <strong className="text-mono">{r.ms_pono}</strong> },
                    { key: "ms_custpo", header: "Customer PO", render: (r) => <span className="text-mono">{r.ms_custpo || "—"}</span> },
                    { key: "cust_po2", header: "End Customer PO Number", accessor: (r) => r.cust_po2 || "",
                      render: (r) => <span className="text-mono">{r.cust_po2 || "—"}</span> },
                    { key: "customer_name", header: "Customer",
                      render: (r) => <span>{r.customer_name}{r.offset_days ? <span className="muted" style={{ fontSize: 10 }}> · −{r.offset_days}d</span> : null}</span> },
                    { key: "status", header: "Status",
                      render: (r) => { const m = STATUS_META[r.status] || { label: r.status, badge: "" }; return <span className={`jde-badge ${m.badge}`}>{m.label}</span>; } },
                    { key: "due_date", header: "Due date", type: "date", accessor: (r) => r.due_date,
                      render: (r) => <span className="text-mono" style={{ fontSize: 11 }}>{fmtDate(r.due_date)}</span> },
                    { key: "target_date", header: "Target", type: "date", accessor: (r) => r.target_date,
                      render: (r) => <span className="text-mono" style={{ fontSize: 11, fontWeight: 600 }}>{fmtDate(r.target_date)}</span> },
                    { key: "ship_date", header: "Ship / Invoice", type: "date", accessor: (r) => r.ship_date,
                      render: (r) => <span className="text-mono" style={{ fontSize: 11 }}>{r.ship_date ? fmtDate(r.ship_date) : <span className="muted">not shipped</span>}</span> },
                    { key: "metric", header: "Days late / to target", align: "right",
                      accessor: (r) => r.kind === "completed" ? (r.days_late || 0) : (r.days_to_target ?? 0),
                      render: (r) => { const m = STATUS_META[r.status] || { color: "#334155" };
                        const t = r.kind === "completed" ? (r.days_late > 0 ? `${r.days_late}d late` : "on time")
                          : (r.days_to_target < 0 ? `${Math.abs(r.days_to_target)}d overdue` : `${r.days_to_target}d left`);
                        return <span className="text-mono" style={{ fontWeight: 700, color: m.color }}>{t}</span>; } },
                    { key: "amount", header: "PO value", align: "right", accessor: (r) => r.amount || 0,
                      render: (r) => <span className="text-mono">{fmtCur(r.amount, r.currency_code || curCode)}</span> },
                    { key: "link", header: "", sortable: false,
                      render: (r) => <Link to={`/pos/${r.ms_pono}`} style={{ color: "var(--omh-primary)", fontSize: 11 }}>open ↗</Link> },
                  ]}
                  rows={visibleRows}
                  getRowKey={(r) => `${r.ms_pono}-${r.ms_custpo}`}
                  defaultSort={{ key: "metric", dir: "desc" }}
                  searchPlaceholder="Search PO #, customer PO, customer…"
                  testid="otd-table"
                  emptyMessage="No POs in this category."
                  renderExpanded={(r) => (
                    <OtdLineDetail pono={r.ms_pono} offsetDays={r.offset_days || 0}
                                   curCode={r.currency_code || curCode} />
                  )}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
